import {
  Component,
  DestroyRef,
  OnDestroy,
  OnInit,
  inject,
  signal,
  viewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { InsightsService } from '@maximizer/core/shared/insights';
import {
  MenuItem,
  OutlookNotificationComponent,
  OutlookSkeletonAttendeeItemComponent,
} from '@maximizer/outlook/shared/ui';
import { CalendarService } from '@maximizer/outlook/main/data-access';
import {
  CalendarStore,
  OutlookService,
  OutlookStore,
  TrackerService,
} from '@maximizer/outlook/shared/data-access';
import {
  CalendarAttendee,
  EntrySearchResult,
  EventAction,
  EventJourney,
  EventMetadata,
  EventModule,
} from '@maximizer/outlook/shared/domain';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AddAppointmentComponent } from '@maximizer/outlook/appointment/ui';
import { KENDO_BUTTONS } from '@progress/kendo-angular-buttons';
import { KENDO_DIALOG } from '@progress/kendo-angular-dialog';
import { KENDO_TOOLTIP } from '@progress/kendo-angular-tooltip';
import { Subject, catchError, concatMap, debounceTime, from, of } from 'rxjs';
import { OutlookCalendarAttendeeItemComponent } from '../attendee-item/attendee-item.component';
import { OutlookCalendarNotInMaximizerListComponent } from '../not-in-maximizer-list/not-in-maximizer-list.component';
import {
  OutlookCalendarSaveAppointmentDirective,
  SaveAppointmentDisabledReason,
} from '../save-appointment.directive';
import { OutlookEntrySearchComponent } from '../../entry-search/entry-search.component';

const RECIPIENTS_CHANGED_DEBOUNCE_MS = 400;

@Component({
  selector: 'maximizer-outlook-calendar-compose',
  imports: [
    AddAppointmentComponent,
    CommonModule,
    KENDO_BUTTONS,
    KENDO_DIALOG,
    KENDO_TOOLTIP,
    TranslatePipe,
    OutlookCalendarAttendeeItemComponent,
    OutlookCalendarNotInMaximizerListComponent,
    OutlookEntrySearchComponent,
    OutlookNotificationComponent,
    OutlookSkeletonAttendeeItemComponent,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './compose.component.html',
})
export class OutlookCalendarComposeComponent
  extends OutlookCalendarSaveAppointmentDirective
  implements OnInit, OnDestroy
{
  readonly notification =
    viewChild.required<OutlookNotificationComponent>('notification');

  searchActive = signal(false);
  eventSavedInOutlook = signal(false);

  buildAttendeeActions = (entry: EntrySearchResult): MenuItem[] => {
    const hasNoEmailAddress = !entry.emailAddress;
    const noEmailAddressReason = hasNoEmailAddress
      ? this.translate.instant('outlook.entrySearch.noEmailAddress')
      : undefined;

    return [
      {
        id: 'required',
        text: this.translate.instant('outlook.calendar.compose.addAsRequired'),
        disabled: hasNoEmailAddress,
        title: noEmailAddressReason,
        click: () => void this.addAttendeeToCalendar(entry, true),
      },
      {
        id: 'optional',
        text: this.translate.instant('outlook.calendar.compose.addAsOptional'),
        disabled: hasNoEmailAddress,
        title: noEmailAddressReason,
        click: () => void this.addAttendeeToCalendar(entry, false),
      },
    ];
  };

  private readonly destroyRef = inject(DestroyRef);
  private readonly recipientsChanged = new Subject<void>();
  private userEmail = '';

  constructor(
    private readonly outlookService: OutlookService,
    calendarService: CalendarService,
    translate: TranslateService,
    private readonly tracker: TrackerService,
    insightsService: InsightsService,
    calendarStore: CalendarStore,
    outlookStore: OutlookStore,
  ) {
    super(
      calendarService,
      translate,
      insightsService,
      calendarStore,
      outlookStore,
    );
  }

  ngOnInit(): void {
    this.outlookStore.loadUsers();

    // One sync at a time: an older Office read must not overwrite a newer attendee list.
    this.recipientsChanged
      .pipe(
        debounceTime(RECIPIENTS_CHANGED_DEBOUNCE_MS),
        concatMap(() =>
          from(this.syncAttendees()).pipe(
            catchError((error) => {
              this.insightsService.trackException({ error: error as Error });
              return of(null);
            }),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    void this.outlookService
      .addRecipientsChangedHandler(() => this.recipientsChanged.next())
      .catch((error) =>
        this.insightsService.trackException({ error: error as Error }),
      );

    void this.initAttendees();
    void this.loadSavedAppointment();
  }

  ngOnDestroy(): void {
    this.outlookService.removeRecipientsChangedHandler();
  }

  protected override pageDisabledReason(): SaveAppointmentDisabledReason | null {
    return this.eventSavedInOutlook() ? null : 'eventNotSavedInOutlook';
  }

  // Probes without saving the item: an unsaved draft has no id and cannot be matched or saved.
  protected override async isEventSavedInOutlook(): Promise<boolean> {
    const itemId = await this.outlookService.getCalendarItemId();
    this.eventSavedInOutlook.set(itemId !== null);
    return itemId !== null;
  }

  async addAttendeeToCalendar(
    entry: EntrySearchResult,
    isRequired: boolean,
  ): Promise<void> {
    if (!entry.emailAddress) {
      return;
    }

    let success = false;

    try {
      this.calendarStore.setLoading(true);

      const attendee: CalendarAttendee = {
        id: entry.id,
        displayName: entry.displayName,
        emailAddress: entry.emailAddress,
        type: entry.type,
        abentryType: entry.abentryType,
        isRequired,
        isInMaximizer: true,
      };

      await this.outlookService.addCalendarAttendee(
        entry.emailAddress,
        entry.displayName,
        isRequired,
      );
      this.calendarStore.addAttendee(attendee);
      this.notification().showMessage(
        'success',
        'outlook.calendar.compose.addAttendee.success',
        true,
      );

      success = true;
    } catch (error) {
      this.notification().showMessage(
        'error',
        'outlook.calendar.compose.addAttendee.error',
        true,
      );
      this.insightsService.trackException({ error: error as Error });
    } finally {
      this.calendarStore.setLoading(false);
      this.tracker.trackEvent({
        module: EventModule.Compose,
        journey: EventJourney.AddRecipient,
        action: EventAction.Completed,
        metadata: {
          attendeeType: isRequired
            ? EventMetadata.Required
            : EventMetadata.Optional,
          result: success ? EventMetadata.Success : EventMetadata.Failed,
        },
      });
    }
  }

  private async initAttendees(): Promise<void> {
    this.calendarStore.setLoading(true);

    try {
      const [attendees, userEmail] = await Promise.all([
        this.outlookService.getCalendarAttendees(),
        this.outlookService.getMailboxEmail(),
      ]);

      this.userEmail = userEmail;

      const filtered = attendees.filter(
        (attendee) => attendee.emailAddress !== userEmail,
      );

      this.calendarStore.updateAttendees(
        await this.calendarService.enrichAttendees(filtered),
      );
    } catch (error) {
      this.insightsService.trackException({ error: error as Error });
    } finally {
      this.calendarStore.setLoading(false);
    }
  }

  private async syncAttendees(): Promise<void> {
    const allOfficeAttendees = await this.outlookService.getCalendarAttendees();
    const officeAttendees = allOfficeAttendees.filter(
      (attendee) => attendee.emailAddress !== this.userEmail,
    );
    const current = this.calendarStore.attendees();

    const newAttendees = officeAttendees.filter(
      (officeAttendee) =>
        !current.some(
          (storedAttendee) =>
            storedAttendee.emailAddress === officeAttendee.emailAddress,
        ),
    );

    const enrichedNew =
      await this.calendarService.enrichAttendees(newAttendees);
    const merged = officeAttendees.map((officeAttendee) => {
      const existing = current.find(
        (storedAttendee) =>
          storedAttendee.emailAddress === officeAttendee.emailAddress,
      );
      if (existing)
        return { ...existing, isRequired: officeAttendee.isRequired };
      return (
        enrichedNew.find(
          (enrichedAttendee) =>
            enrichedAttendee.emailAddress === officeAttendee.emailAddress,
        ) ?? officeAttendee
      );
    });

    this.calendarStore.updateAttendees(merged);
  }
}
